// Shared between InterviewSessions.tsx and SessionActionsDialog.tsx.
// Kept in its own module rather than duplicated per file (the convention
// elsewhere in this app) because the list imports the dialog, so exporting
// these from either component would create a circular import.

/**
 * The minimum a session row must carry to drive the recovery dialogs.
 *
 * Declared structurally rather than tied to one endpoint because two pages feed
 * those dialogs: this folder's interview browser, and Company Hub's Interviews
 * tab, which pages the same /super-admin/interview-sessions/ list filtered by
 * company. Both use SuperAdminInterviewSessionSerializer (pipeline health via
 * InterviewPipelineHealthMixin), so the health fields below mean the same
 * thing in either.
 */
export interface ManageableSession {
    id: number;
    status: string;
    candidate_name: string | null;
    candidate_email: string | null;
    job_role: string;
    job_description_title: string | null;
    /** Absent from Company Hub's rows, which are already company-scoped. */
    company_name?: string | null;
    effective_score: number | null;
    // Pipeline health
    question_video_count: number;
    has_session_recording: boolean;
    has_feedback: boolean;
    questions_total: number;
    unscored_question_count: number;
}

export interface InterviewSession extends ManageableSession {
    candidate: number | null;
    company: number | null;
    company_name: string | null;
    job_description: number | null;
    status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
    started_at: string | null;
    ended_at: string | null;
    total_score: number | null;
    invite_based: boolean;
    is_archived: boolean;
    credits_used: number;
}

export interface SessionQuestionRow {
    id: number;
    order: number;
    question_text: string | null;
    question_type: string | null;
    skill_tag: string | null;
    score: number | null;
    evaluation: string | null;
    plagiarism: string | null;
    confidence: number | null;
    answer_source: string | null;
    answered_at: string | null;
    has_answer: boolean;
    has_question_video: boolean;
    excluded_from_scoring: boolean;
    can_reevaluate: boolean;
    reevaluate_blocked_reason: string | null;
}

// --- Assessment report ---------------------------------------------------

/**
 * Response of /super-admin/interview-sessions/<id>/report/.
 *
 * Both halves go straight to the ported ViewResult component, which is shared
 * with the recruiter app and reads the raw, unvalidated JSON that the
 * generate_feedback task wrote. Restating those shapes here would be a second,
 * inevitably drifting description of a contract this app does not own, so the
 * nested payloads stay deliberately loose.
 */
export interface SessionReportResponse {
    /** interviews.FeedbackSerializer, minus its nested session copy. */
    feedback: {
        id: number;
        summary: string | null;
        detailed_breakdown: Record<string, any> | null;
        created_at: string;
        language_name: string | null;
        /** False when the row holds a single answer's evaluation, not the report. */
        is_full_session_report: boolean;
        [key: string]: unknown;
    } | null;
    /** interviews.InterviewSessionSerializer. */
    session: Record<string, any> | null;
}

export type JobKind = 'recording' | 'feedback' | 'question';

export interface ActiveJob {
    kind: JobKind;
    taskId: string;
    /**
     * Earlier task in a chained job. A failure there leaves taskId PENDING
     * forever, so the poller watches this one too.
     */
    upstreamTaskId?: string;
    /** session_question_id, for kind === 'question'. */
    targetId?: number;
}

export interface JobStatus {
    task_id: string;
    state: string;
    ready: boolean;
    result: unknown;
    error: string | null;
}

export const JOB_LABELS: Record<JobKind, string> = {
    recording: 'Creating full recording',
    feedback: 'Regenerating feedback',
    question: 'Re-evaluating answer and rebuilding report',
};
